import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { db, storage } from '../../../firebase/config';
import { callClaude } from '../../../utils/claudeProxy';
import {
  doc, onSnapshot, collection, addDoc, deleteDoc, updateDoc,
  serverTimestamp, getDocs, writeBatch, increment, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import {
  Box, Typography, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip, Slider, CircularProgress,
  Paper, Chip, Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PrintIcon from '@mui/icons-material/Print';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import BoltIcon from '@mui/icons-material/Bolt';
import StyleIcon from '@mui/icons-material/Style';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupIcon from '@mui/icons-material/Group';
import DevicesIcon from '@mui/icons-material/Devices';
import SwotNGuide, { SwotNHelpButton } from '../components/SwotNGuide';
import { useCompanyLogo } from '../../../hooks/useQRLogo';
import { GROUP_COLORS } from '../../workshop/utils/sessionCode';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

const accent = '#E05500';

const MEDAL = {
  gold:   { color: '#FFD700', label: 'Guld',   bg: '#FFD70022', border: '#FFD70066' },
  silver: { color: '#C0C0C0', label: 'Silver',  bg: '#C0C0C022', border: '#C0C0C066' },
  bronze: { color: '#CD7F32', label: 'Brons',   bg: '#CD7F3222', border: '#CD7F3266' },
};
const MEDAL_CYCLE = [null, 'gold', 'silver', 'bronze'];

// ── Färger per kvadrant ────────────────────────────────────────────────────────
export const QUAD = {
  strength:    { label: 'Styrkor',     letter: 'S', color: '#4CAF50', dark: '#1B5E20', light: '#A5D6A7', Icon: EmojiEventsIcon },
  weakness:    { label: 'Svagheter',   letter: 'W', color: '#EF5350', dark: '#B71C1C', light: '#FFCDD2', Icon: WarningAmberIcon },
  opportunity: { label: 'Möjligheter', letter: 'O', color: '#42A5F5', dark: '#0D47A1', light: '#BBDEFB', Icon: TipsAndUpdatesIcon },
  threat:      { label: 'Hot',         letter: 'T', color: '#E05500', dark: '#7F2700', light: '#FFCCBC', Icon: BoltIcon },
};

const QUADS = ['strength', 'weakness', 'opportunity', 'threat'];

// ── Hjälpfunktioner ────────────────────────────────────────────────────────────
function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#')) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if (isNaN(r)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${r},${g},${b},${+alpha.toFixed(3)})`;
}

function makeGlass(color, opacity, blur) {
  const op = opacity / 100;
  return {
    background: color
      ? `linear-gradient(135deg, ${hexToRgba(color, op * 1.5)} 0%, ${hexToRgba(color, op * 0.5)} 100%)`
      : `rgba(20,20,20,${op})`,
    backdropFilter: `blur(${blur}px) saturate(150%)`,
    WebkitBackdropFilter: `blur(${blur}px) saturate(150%)`,
    border: `1px solid ${color ? hexToRgba(color, 0.3) : 'rgba(255,255,255,0.1)'}`,
    borderRadius: '10px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
  };
}

// ── Kort-komponent ─────────────────────────────────────────────────────────────
function SwotCard({ card, quadrant, onDelete, onEdit, presentMode, currentUserId, onToggleStar, allCards, onCycleRank }) {
  const q = QUAD[quadrant];
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);
  const stars = card.stars || [];
  const starred = currentUserId && stars.includes(currentUserId);
  const quadrantStarCount = (allCards || []).filter(c => c.quadrant === quadrant && (c.stars || []).includes(currentUserId)).length;
  const canStar = starred || quadrantStarCount < 3;
  const rank = card.rank || null;
  const medal = rank ? MEDAL[rank] : null;

  return (
    <Box
      draggable={!presentMode}
      onDragStart={e => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('cardId', card.id);
        e.dataTransfer.setData('fromQuadrant', quadrant);
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        position: 'relative',
        background: medal ? medal.bg : (hover && !presentMode ? hexToRgba(q.color, 0.18) : hexToRgba(q.color, 0.12)),
        border: medal ? `1px solid ${medal.border}` : `1px solid ${hexToRgba(q.color, hover && !presentMode ? 0.55 : 0.2)}`,
        borderRadius: '10px',
        p: presentMode ? '7px 10px' : '8px 10px',
        pt: !presentMode ? '22px' : (medal ? '20px' : '7px'),
        fontSize: presentMode ? '0.78rem' : '0.82rem',
        color: 'rgba(255,255,255,0.9)',
        lineHeight: 1.45,
        transition: 'border-color 0.15s, opacity 0.15s, background 0.15s, box-shadow 0.15s',
        borderLeft: medal ? `3px solid ${medal.color}` : `3px solid ${q.color}`,
        boxShadow: medal
          ? `0 0 0 1px ${medal.border}, 0 2px 14px ${medal.color}22`
          : (hover && !presentMode ? `0 0 0 1px ${hexToRgba(q.color, 0.35)}, 0 2px 12px ${hexToRgba(q.color, 0.2)}` : 'none'),
        opacity: dragging ? 0.35 : 1,
        cursor: presentMode ? 'default' : 'grab',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      {/* Medal badge (present mode) */}
      {medal && presentMode && (
        <Box sx={{ position: 'absolute', top: 4, right: 6, display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <EmojiEventsIcon sx={{ fontSize: 13, color: medal.color }} />
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: medal.color, letterSpacing: 0.5 }}>
            {medal.label.toUpperCase()}
          </Typography>
        </Box>
      )}

      {/* Top bar: edit/delete left, medal+star right */}
      {!presentMode && (
        <Box sx={{ position: 'absolute', top: 3, left: 3, right: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 0.25, opacity: hover ? 1 : 0, transition: 'opacity 0.15s' }}>
            <IconButton size="small" onClick={() => onEdit(card)}
              sx={{ width: 18, height: 18, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
              <EditIcon sx={{ fontSize: 11 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(card.id)}
              sx={{ width: 18, height: 18, color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444' } }}>
              <DeleteIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {/* Medal toggle */}
            {onCycleRank && (
              <Tooltip title={rank ? `${medal.label} — klicka för att ändra` : 'Sätt medalj'}>
                <IconButton size="small"
                  onClick={e => { e.stopPropagation(); onCycleRank(card.id, rank); }}
                  sx={{ width: 18, height: 18, p: 0,
                    color: medal ? medal.color : 'rgba(255,255,255,0.18)',
                    opacity: medal ? 1 : (hover ? 0.7 : 0),
                    transition: 'opacity 0.15s',
                    '&:hover': { color: '#FFD700' },
                  }}>
                  <EmojiEventsIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Vote star */}
            {currentUserId && (
              <>
                {stars.length > 0 && (
                  <Typography fontSize="0.6rem" color="#FFD700" lineHeight={1}>{stars.length}</Typography>
                )}
                <IconButton size="small"
                  onClick={e => { e.stopPropagation(); if (canStar) onToggleStar(card.id, quadrant); }}
                  sx={{ width: 18, height: 18, p: 0, color: starred ? '#FFD700' : 'rgba(255,255,255,0.2)',
                    '&:hover': { color: canStar ? '#FFD700' : 'rgba(255,255,255,0.2)' },
                    cursor: canStar ? 'pointer' : 'not-allowed',
                  }}>
                  {starred ? <StarIcon sx={{ fontSize: 12 }} /> : <StarBorderIcon sx={{ fontSize: 12 }} />}
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      )}
      <Typography fontSize="inherit" lineHeight="inherit">{card.text}</Typography>
      {card.source === 'workshopN' && (
        <Typography fontSize="0.6rem" color={q.light} mt={0.25} sx={{ opacity: 0.6 }}>
          importerat från WorkshopN
        </Typography>
      )}
    </Box>
  );
}

// ── Klassisk 2×2 ──────────────────────────────────────────────────────────────
function ClassicTemplate({ cards, presentMode, panelOpacity, panelBlur, onAddCard, onDeleteCard, onEditCard, onMoveCard, currentUserId, onToggleStar, onCycleRank }) {
  const panels = [
    { q: 'strength',    right: false },
    { q: 'weakness',    right: true  },
    { q: 'opportunity', right: false },
    { q: 'threat',      right: true  },
  ];
  const [dragOver, setDragOver] = useState(null);

  return (
    <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '10px', width: '100%', height: '100%' }}>

      {/* Center diamond hub */}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%) rotate(45deg)',
        width: 124, height: 124, zIndex: 10,
        background: 'linear-gradient(135deg, #1e1e1e, #0a0a0a)',
        border: `3px solid ${accent}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 0 36px ${hexToRgba(accent, 0.55)}`,
        pointerEvents: 'none',
      }}>
        <Typography sx={{ transform: 'rotate(-45deg)', fontSize: '1.75rem', fontWeight: 900, color: accent, letterSpacing: 2 }}>SWOT</Typography>
      </Box>

      {panels.map(({ q, right }) => {
        const info = QUAD[q];
        const qCards = cards.filter(c => c.quadrant === q);
        const isOver = dragOver === q;
        return (
          <Box key={q}
            onDragOver={e => { e.preventDefault(); setDragOver(q); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={e => {
              const cardId = e.dataTransfer.getData('cardId');
              const from = e.dataTransfer.getData('fromQuadrant');
              setDragOver(null);
              if (from !== q && cardId) onMoveCard(cardId, q);
            }}
            sx={{
              ...makeGlass(info.dark, panelOpacity, panelBlur),
              p: '14px 16px',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              outline: isOver ? `2px solid ${info.color}` : '2px solid transparent',
              transition: 'outline-color 0.15s',
            }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexShrink: 0,
              flexDirection: right ? 'row-reverse' : 'row',
            }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px',
                bgcolor: hexToRgba(info.color, 0.22),
                border: `1.5px solid ${hexToRgba(info.color, 0.45)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <info.Icon sx={{ fontSize: 30, color: info.color }} />
              </Box>
              <Typography sx={{
                fontWeight: 900, fontSize: '1.1rem', color: info.light,
                letterSpacing: 1.5, textTransform: 'uppercase',
                flex: 1, textAlign: right ? 'right' : 'left',
              }}>
                {info.label}
              </Typography>
            </Box>

            {/* Kort */}
            <Box sx={{
              flex: 1, overflow: 'auto', pr: 0.5,
              display: 'grid',
              gridTemplateColumns: '1fr',
              gridAutoRows: 'min-content',
              gap: 0.75,
              alignContent: 'start',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: hexToRgba(info.color, 0.3), borderRadius: 2 },
            }}>
              {qCards.map(c => (
                <SwotCard key={c.id} card={c} quadrant={q} presentMode={presentMode}
                  onDelete={onDeleteCard} onEdit={onEditCard}
                  currentUserId={currentUserId} onToggleStar={onToggleStar} allCards={cards} onCycleRank={onCycleRank} />
              ))}
              {!presentMode && qCards.length === 0 && (
                <Typography fontSize="0.72rem" color="rgba(255,255,255,0.2)" fontStyle="italic" pt={0.5}
                  textAlign={right ? 'right' : 'left'}>
                  Inga bidrag ännu
                </Typography>
              )}
            </Box>

            {/* + knapp nederkant */}
            {!presentMode && (
              <Button size="small"
                startIcon={!right ? <AddIcon sx={{ fontSize: '0.95rem !important' }} /> : undefined}
                endIcon={right ? <AddIcon sx={{ fontSize: '0.95rem !important' }} /> : undefined}
                onClick={() => onAddCard(q)}
                sx={{
                  mt: 1, color: info.color, textTransform: 'none', fontSize: '0.75rem', flexShrink: 0,
                  justifyContent: right ? 'flex-end' : 'flex-start',
                  p: '3px 6px', borderRadius: '8px',
                  border: `1px dashed ${hexToRgba(info.color, 0.3)}`,
                  '&:hover': { bgcolor: hexToRgba(info.color, 0.1), borderColor: info.color },
                }}>
                Lägg till
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}



// ── Kolumner-template ──────────────────────────────────────────────────────────
function ColumnsTemplate({ cards, presentMode, panelOpacity, panelBlur, onAddCard, onDeleteCard, onEditCard, onMoveCard, currentUserId, onToggleStar, onCycleRank }) {
  const [dragOver, setDragOver] = useState(null);
  return (
    <Box sx={{ display: 'flex', gap: '8px', width: '100%', height: '100%' }}>
      {QUADS.map(q => {
        const info = QUAD[q];
        const qCards = cards.filter(c => c.quadrant === q);
        const isOver = dragOver === q;
        return (
          <Box key={q}
            onDragOver={e => { e.preventDefault(); setDragOver(q); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={e => {
              const cardId = e.dataTransfer.getData('cardId');
              const from = e.dataTransfer.getData('fromQuadrant');
              setDragOver(null);
              if (from !== q && cardId) onMoveCard(cardId, q);
            }}
            sx={{
              flex: 1,
              ...makeGlass(info.dark, panelOpacity, panelBlur),
              p: '16px 12px',
              display: 'flex', flexDirection: 'column',
              borderTop: `3px solid ${info.color}`,
              outline: isOver ? `2px solid ${info.color}` : '2px solid transparent',
              transition: 'outline-color 0.15s',
            }}>
            {/* Header — Columns */}
            <Box sx={{ textAlign: 'center', mb: 1.5, flexShrink: 0 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px', mx: 'auto', mb: 0.75,
                bgcolor: hexToRgba(info.color, 0.2),
                border: `1.5px solid ${hexToRgba(info.color, 0.45)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <info.Icon sx={{ fontSize: 30, color: info.color }} />
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: info.light, letterSpacing: 1.2, textTransform: 'uppercase' }}>
                {info.label}
              </Typography>
            </Box>
            {/* Kort */}
            <Box sx={{
              flex: 1, overflow: 'auto',
              display: 'grid',
              gridTemplateColumns: '1fr',
              gridAutoRows: 'min-content',
              gap: 0.75,
              alignContent: 'start',
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: hexToRgba(info.color, 0.3), borderRadius: 2 },
            }}>
              {qCards.map(c => (
                <SwotCard key={c.id} card={c} quadrant={q} presentMode={presentMode}
                  onDelete={onDeleteCard} onEdit={onEditCard}
                  currentUserId={currentUserId} onToggleStar={onToggleStar} allCards={cards} onCycleRank={onCycleRank} />
              ))}
              {!presentMode && qCards.length === 0 && (
                <Typography fontSize="0.7rem" color="rgba(255,255,255,0.2)" fontStyle="italic" textAlign="center" mt={1}>
                  Tom
                </Typography>
              )}
            </Box>
            {/* Lägg till */}
            {!presentMode && (
              <Button
                size="small" startIcon={<AddIcon />}
                onClick={() => onAddCard(q)}
                sx={{ mt: 1, color: info.color, textTransform: 'none', fontSize: '0.72rem', flexShrink: 0,
                  border: `1px dashed ${hexToRgba(info.color, 0.3)}`,
                  borderRadius: 2,
                  '&:hover': { bgcolor: hexToRgba(info.color, 0.1) }
                }}
              >
                Lägg till
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}


// ── Vivid card (behöver eget hover-state utanför map) ─────────────────────────
function VividCard({ card, quadrant, presentMode, onEdit, onDelete, currentUserId, onToggleStar, allCards, onCycleRank }) {
  const [hov, setHov] = useState(false);
  const [dragging, setDragging] = useState(false);
  const stars = card.stars || [];
  const starred = currentUserId && stars.includes(currentUserId);
  const quadrantStarCount = (allCards || []).filter(c => c.quadrant === quadrant && (c.stars || []).includes(currentUserId)).length;
  const canStar = starred || quadrantStarCount < 3;
  const rank = card.rank || null;
  const medal = rank ? MEDAL[rank] : null;
  return (
    <Box
      draggable={!presentMode}
      onDragStart={e => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('cardId', card.id);
        e.dataTransfer.setData('fromQuadrant', quadrant);
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      sx={{
        position: 'relative',
        background: medal ? medal.bg : (hov && !presentMode ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.15)'),
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        border: medal ? `1px solid ${medal.border}` : `1px solid ${hov && !presentMode ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.25)'}`,
        borderRadius: '8px',
        p: presentMode ? '6px 10px' : '7px 10px',
        pt: !presentMode ? '22px' : (medal ? '20px' : '6px'),
        fontSize: presentMode ? '0.78rem' : '0.82rem',
        color: '#fff',
        lineHeight: 1.45,
        borderLeft: medal ? `3px solid ${medal.color}` : '3px solid rgba(255,255,255,0.5)',
        boxShadow: medal
          ? `0 0 0 1px ${medal.border}, 0 2px 14px ${medal.color}22`
          : (hov && !presentMode ? '0 0 0 1px rgba(255,255,255,0.2), 0 2px 12px rgba(255,255,255,0.1)' : 'none'),
        transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s, opacity 0.15s',
        opacity: dragging ? 0.35 : 1,
        cursor: presentMode ? 'default' : 'grab',
        '&:active': { cursor: 'grabbing' },
      }}>
      {/* Medal badge (present mode) */}
      {medal && presentMode && (
        <Box sx={{ position: 'absolute', top: 4, right: 6, display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <EmojiEventsIcon sx={{ fontSize: 13, color: medal.color }} />
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: medal.color, letterSpacing: 0.5 }}>
            {medal.label.toUpperCase()}
          </Typography>
        </Box>
      )}
      {/* Top bar: edit/delete left, medal+star right */}
      {!presentMode && (
        <Box sx={{ position: 'absolute', top: 3, left: 3, right: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 0.25, opacity: hov ? 1 : 0, transition: 'opacity 0.15s' }}>
            <IconButton size="small" onClick={() => onEdit(card)}
              sx={{ width: 18, height: 18, color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}>
              <EditIcon sx={{ fontSize: 11 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(card.id)}
              sx={{ width: 18, height: 18, color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#ffcdd2' } }}>
              <DeleteIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {onCycleRank && (
              <Tooltip title={rank ? `${medal.label} — klicka för att ändra` : 'Sätt medalj'}>
                <IconButton size="small"
                  onClick={e => { e.stopPropagation(); onCycleRank(card.id, rank); }}
                  sx={{ width: 18, height: 18, p: 0,
                    color: medal ? medal.color : 'rgba(255,255,255,0.25)',
                    opacity: medal ? 1 : (hov ? 0.7 : 0),
                    transition: 'opacity 0.15s',
                    '&:hover': { color: '#FFD700' },
                  }}>
                  <EmojiEventsIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            )}
            {currentUserId && (
              <>
                {stars.length > 0 && (
                  <Typography fontSize="0.6rem" color="#FFD700" lineHeight={1}>{stars.length}</Typography>
                )}
                <IconButton size="small"
                  onClick={e => { e.stopPropagation(); if (canStar) onToggleStar(card.id, quadrant); }}
                  sx={{ width: 18, height: 18, p: 0, color: starred ? '#FFD700' : 'rgba(255,255,255,0.35)',
                    '&:hover': { color: canStar ? '#FFD700' : 'rgba(255,255,255,0.35)' },
                    cursor: canStar ? 'pointer' : 'not-allowed',
                  }}>
                  {starred ? <StarIcon sx={{ fontSize: 12 }} /> : <StarBorderIcon sx={{ fontSize: 12 }} />}
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      )}
      <Typography fontSize="inherit" lineHeight="inherit">{card.text}</Typography>
    </Box>
  );
}

// ── Vivid-template (helfärgade kvadranter) ─────────────────────────────────────
function VividTemplate({ cards, presentMode, panelOpacity, panelBlur, onAddCard, onDeleteCard, onEditCard, onMoveCard, currentUserId, onToggleStar, onCycleRank }) {
  const panels = [
    { q: 'strength',    right: false },
    { q: 'weakness',    right: true  },
    { q: 'opportunity', right: false },
    { q: 'threat',      right: true  },
  ];
  const [dragOver, setDragOver] = useState(null);
  return (
    <Box sx={{
      position: 'relative',
      display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
      gap: '3px', width: '100%', height: '100%', borderRadius: '10px', overflow: 'hidden',
    }}>
      {/* Central diamond hub */}
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%,-50%) rotate(45deg)',
        width: 124, height: 124, zIndex: 30,
        background: 'linear-gradient(135deg, #1e1e1e, #0a0a0a)',
        border: `3px solid ${accent}`,
        boxShadow: `0 0 36px ${hexToRgba(accent, 0.55)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        pointerEvents: 'none',
      }}>
        <Typography sx={{ transform: 'rotate(-45deg)', fontSize: '1.75rem', fontWeight: 900, color: accent, letterSpacing: 2 }}>SWOT</Typography>
      </Box>

      {panels.map(({ q, right }) => {
        const info = QUAD[q];
        const qCards = cards.filter(c => c.quadrant === q);
        const isOver = dragOver === q;
        return (
          <Box key={q}
            onDragOver={e => { e.preventDefault(); setDragOver(q); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={e => {
              const cardId = e.dataTransfer.getData('cardId');
              const from = e.dataTransfer.getData('fromQuadrant');
              setDragOver(null);
              if (from !== q && cardId) onMoveCard(cardId, q);
            }}
            sx={{
              background: `linear-gradient(145deg, ${info.color} 0%, ${info.dark} 100%)`,
              p: '14px 16px',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              position: 'relative',
              outline: isOver ? '3px solid rgba(255,255,255,0.7)' : '3px solid transparent',
              transition: 'outline-color 0.15s',
            }}>
            {/* Watermark letter */}
            <Typography sx={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              fontSize: '8rem', fontWeight: 900, lineHeight: 1,
              color: 'rgba(255,255,255,0.06)',
              fontFamily: "'DM Sans', sans-serif",
              pointerEvents: 'none', userSelect: 'none',
            }}>
              {info.letter}
            </Typography>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexShrink: 0, zIndex: 1,
              flexDirection: right ? 'row-reverse' : 'row',
            }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '14px',
                bgcolor: 'rgba(255,255,255,0.2)',
                border: '1.5px solid rgba(255,255,255,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <info.Icon sx={{ fontSize: 30, color: '#fff' }} />
              </Box>
              <Typography sx={{
                fontWeight: 900, fontSize: '1.1rem', color: '#fff',
                letterSpacing: 1.5, textTransform: 'uppercase',
                flex: 1, textAlign: right ? 'right' : 'left',
              }}>
                {info.label}
              </Typography>
            </Box>

            {/* Cards */}
            <Box sx={{
              flex: 1, overflow: 'auto', zIndex: 1,
              display: 'grid',
              gridTemplateColumns: '1fr',
              gridAutoRows: 'min-content',
              gap: 0.75,
              alignContent: 'start',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 2 },
            }}>
              {qCards.map(c => (
                <VividCard key={c.id} card={c} quadrant={q} presentMode={presentMode}
                  onEdit={onEditCard} onDelete={onDeleteCard}
                  currentUserId={currentUserId} onToggleStar={onToggleStar} allCards={cards} onCycleRank={onCycleRank} />
              ))}
              {!presentMode && qCards.length === 0 && (
                <Typography fontSize="0.72rem" color="rgba(255,255,255,0.4)" fontStyle="italic" pt={0.5}
                  textAlign={right ? 'right' : 'left'}>
                  Inga bidrag ännu
                </Typography>
              )}
            </Box>

            {/* + knapp nederkant */}
            {!presentMode && (
              <Button size="small"
                startIcon={!right ? <AddIcon sx={{ fontSize: '0.95rem !important' }} /> : undefined}
                endIcon={right ? <AddIcon sx={{ fontSize: '0.95rem !important' }} /> : undefined}
                onClick={() => onAddCard(q)}
                sx={{
                  mt: 1, color: '#fff', textTransform: 'none', fontSize: '0.75rem', flexShrink: 0, zIndex: 1,
                  justifyContent: right ? 'flex-end' : 'flex-start',
                  p: '3px 6px', borderRadius: '8px',
                  border: '1px dashed rgba(255,255,255,0.35)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.6)' },
                }}>
                Lägg till
              </Button>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

// ── In-board timercirkel ──────────────────────────────────────────────────────
function BoardTimerCircle({ quadrant, totalSeconds, onClose }) {
  const info = QUAD[quadrant] || {};
  const [secs, setSecs] = useState(totalSeconds);
  const [running, setRunning] = useState(true);
  const intervalRef = useRef(null);

  // Web Audio alarm
  const playAlarm = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[0, 440], [0.25, 550], [0.5, 660], [0.75, 440], [1.0, 330]].forEach(([t, freq]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = 'square'; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.18, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.22);
        osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + 0.25);
      });
    } catch (_) {}
  };

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecs(s => {
        if (s <= 1) { clearInterval(intervalRef.current); playAlarm(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const pct = totalSeconds > 0 ? secs / totalSeconds : 0;
  const r = 68;
  const circ = 2 * Math.PI * r;
  const fillColor = secs <= 60 ? '#ef4444' : secs <= 120 ? '#FF9800' : (info.color || accent);

  return (
    <Box sx={{
      position: 'absolute', top: '50%', left: '50%',
      transform: 'translate(-50%,-50%)',
      zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <Box sx={{
        position: 'relative', width: 172, height: 172,
        background: 'rgba(8,8,8,0.88)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '50%',
        boxShadow: `0 0 40px ${fillColor}55, 0 0 0 2px ${fillColor}33`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        {/* SVG progress ring */}
        <Box component="svg" width={172} height={172}
          sx={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}>
          <circle cx={86} cy={86} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={5} />
          <circle cx={86} cy={86} r={r} fill="none" stroke={fillColor} strokeWidth={5}
            strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
            style={{ transition: 'stroke-dashoffset 0.9s linear, stroke 0.5s' }}
          />
        </Box>

        {/* Quadrant icon */}
        {info.Icon && <info.Icon sx={{ fontSize: 18, color: fillColor, mb: 0.5, zIndex: 1, opacity: 0.7 }} />}

        {/* Time display */}
        <Typography sx={{
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontSize: '2.4rem', fontWeight: 900, letterSpacing: '-1px',
          color: fillColor, lineHeight: 1, zIndex: 1,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {mm}:{ss}
        </Typography>

        {/* Label */}
        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', mt: 0.5, zIndex: 1, letterSpacing: 1, textTransform: 'uppercase' }}>
          {info.label}
        </Typography>

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1, zIndex: 1 }}>
          <IconButton size="small" onClick={() => setRunning(r => !r)}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
            {running ? <Box sx={{ width: 8, height: 8, bgcolor: 'currentColor', borderRadius: '1px' }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
          </IconButton>
          <IconButton size="small" onClick={onClose}
            sx={{ width: 26, height: 26, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)',
              '&:hover': { bgcolor: '#ef444422', color: '#ef4444' } }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

// ── AI-analys ──────────────────────────────────────────────────────────────────
function SwotAIAnalysis({ session, cards }) {
  const [loading,  setLoading]  = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState('');

  const analyze = async () => {
    if (cards.length === 0) return;
    setLoading(true); setError(''); setResult(null);

    const grouped = QUADS.reduce((acc, q) => {
      acc[q] = cards.filter(c => c.quadrant === q).map(c => c.text);
      return acc;
    }, {});

    const prompt = `Du är en erfaren strategikonsult. Analysera följande SWOT-material på svenska.
${session.question ? `\nFRÅGESTÄLLNING: ${session.question}\n` : ''}
STYRKOR:\n${grouped.strength.map(t => `- ${t}`).join('\n') || '(inga)'}

SVAGHETER:\n${grouped.weakness.map(t => `- ${t}`).join('\n') || '(inga)'}

MÖJLIGHETER:\n${grouped.opportunity.map(t => `- ${t}`).join('\n') || '(inga)'}

HOT:\n${grouped.threat.map(t => `- ${t}`).join('\n') || '(inga)'}

Returnera ENBART ett JSON-objekt (inga kodblock):
{
  "sammanfattning": "2-3 meningar om den strategiska situationen",
  "teman": { "styrkor": ["tema"], "svagheter": ["tema"], "möjligheter": ["tema"], "hot": ["tema"] },
  "korsningar": [
    { "typ": "SO|ST|WO|WT", "titel": "kort titel", "beskrivning": "vad det innebär strategiskt", "prioritet": "hög|medel|låg" }
  ],
  "åtgärder": [
    { "handling": "konkret och specifik handling (verb + objekt)", "motivering": "varför detta är kritiskt", "ansvarig": "specifik roll eller person, aldrig 'vi' eller 'teamet'", "datum": "konkret datum i formatet ÅÅÅÅ-MM-DD (ca 30-90 dagar)", "uppföljning": "hur och när detta följs upp på nästa möte", "prioritet": "hög|medel|låg" }
  ]
}
Max 5 åtgärder, fokus på de mest prioriterade.`;

    try {
      const data = await callClaude({
        model: 'claude-sonnet-4-6',
        max_tokens: 2500,
        messages: [{ role: 'user', content: prompt }],
      });
      const raw = data.content?.[0]?.text || '';
      setResult(JSON.parse(raw.replace(/```json|```/g, '').trim()));
    } catch (e) {
      setError('Analysen misslyckades.');
    } finally {
      setLoading(false);
    }
  };

  const crossColor = (typ) => {
    if (typ === 'SO') return '#4CAF50';
    if (typ === 'ST') return '#42A5F5';
    if (typ === 'WO') return '#FF9800';
    return '#EF5350';
  };

  const prioBadge = (p) => ({
    hög:  { bg: '#ef444422', color: '#f87171' },
    medel:{ bg: '#f59e0b22', color: '#fbbf24' },
    låg:  { bg: '#6b728022', color: '#9ca3af' },
  })[p] || { bg: '#6b728022', color: '#9ca3af' };

  return (
    <Box>
      {!result && (
        <Button
          variant="outlined"
          onClick={analyze}
          disabled={loading || cards.length === 0}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: accent }} /> : <AutoAwesomeIcon />}
          sx={{ borderColor: accent, color: accent, textTransform: 'none', fontWeight: 600 }}
        >
          {loading ? 'Analyserar…' : 'Analysera med AI'}
        </Button>
      )}
      {error && <Typography color="error" fontSize="0.82rem" mt={1}>{error}</Typography>}

      {result && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Sammanfattning */}
          <Paper sx={{ p: 2.5, borderRadius: 2, borderLeft: `4px solid ${accent}`, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography fontSize="0.68rem" fontWeight={700} letterSpacing={1} color="rgba(255,255,255,0.45)" mb={0.5}>STRATEGISK SAMMANFATTNING</Typography>
            <Typography fontSize="0.92rem" lineHeight={1.7} color="#fff">{result.sammanfattning}</Typography>
          </Paper>

          {/* Teman */}
          {result.teman && (
            <Box>
              <Typography fontSize="0.68rem" fontWeight={700} letterSpacing={1} color="rgba(255,255,255,0.45)" mb={1.5}>TEMAN PER KVADRANT</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {Object.entries({ styrkor: 'strength', svagheter: 'weakness', möjligheter: 'opportunity', hot: 'threat' }).map(([key, q]) => {
                  const info = QUAD[q];
                  const teman = result.teman?.[key] || [];
                  return (
                    <Box key={q} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${info.color}33`, bgcolor: info.color + '14' }}>
                      <Typography fontSize="0.68rem" fontWeight={700} color={info.color} letterSpacing={1} mb={0.75}>{info.label.toUpperCase()}</Typography>
                      {teman.map((t, i) => (
                        <Typography key={i} fontSize="0.8rem" color="rgba(255,255,255,0.7)" sx={{ mb: 0.25, '&:before': { content: '"→ "', color: info.color } }}>{t}</Typography>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Korskopplingar */}
          {result.korsningar?.length > 0 && (
            <Box>
              <Typography fontSize="0.68rem" fontWeight={700} letterSpacing={1} color="rgba(255,255,255,0.45)" mb={1.5}>STRATEGISKA KORSKOPPLINGAR</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.korsningar.map((k, i) => {
                  const p = prioBadge(k.prioritet);
                  return (
                    <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.1)', bgcolor: 'rgba(255,255,255,0.05)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{ px: 1, py: 0.2, borderRadius: 1, bgcolor: crossColor(k.typ) + '22', border: `1px solid ${crossColor(k.typ)}44` }}>
                          <Typography fontSize="0.68rem" fontWeight={800} color={crossColor(k.typ)}>{k.typ}</Typography>
                        </Box>
                        <Typography fontWeight={600} fontSize="0.85rem" flex={1} color="#fff">{k.titel}</Typography>
                        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, bgcolor: p.bg }}>
                          <Typography fontSize="0.65rem" fontWeight={700} color={p.color}>{k.prioritet}</Typography>
                        </Box>
                      </Box>
                      <Typography fontSize="0.8rem" color="rgba(255,255,255,0.6)" lineHeight={1.5}>{k.beskrivning}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Åtgärder */}
          {result.åtgärder?.length > 0 && (
            <Box>
              <Typography fontSize="0.68rem" fontWeight={700} letterSpacing={1} color="rgba(255,255,255,0.45)" mb={1.5}>PRIORITERADE ÅTGÄRDER (MAX 5)</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {result.åtgärder.map((å, i) => {
                  const p = prioBadge(å.prioritet);
                  return (
                    <Box key={i} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
                        <Box sx={{ px: 0.75, py: 0.2, borderRadius: 1, bgcolor: p.bg, flexShrink: 0, mt: 0.2 }}>
                          <Typography fontSize="0.65rem" fontWeight={700} color={p.color}>{å.prioritet}</Typography>
                        </Box>
                        <Typography fontWeight={700} fontSize="0.88rem" color="#fff" flex={1}>{å.handling}</Typography>
                      </Box>
                      <Typography fontSize="0.78rem" color="rgba(255,255,255,0.6)" mb={0.75}>{å.motivering}</Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        {å.ansvarig && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography fontSize="0.65rem" color="rgba(255,255,255,0.35)">Ansvarig:</Typography>
                            <Typography fontSize="0.72rem" fontWeight={600} color="rgba(255,255,255,0.75)">{å.ansvarig}</Typography>
                          </Box>
                        )}
                        {å.datum && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography fontSize="0.65rem" color="rgba(255,255,255,0.35)">Datum:</Typography>
                            <Typography fontSize="0.72rem" fontWeight={600} color="#42A5F5">{å.datum}</Typography>
                          </Box>
                        )}
                      </Box>
                      {å.uppföljning && (
                        <Box sx={{ mt: 0.75, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                          <Typography fontSize="0.65rem" color="rgba(255,255,255,0.35)" mb={0.25}>UPPFÖLJNING</Typography>
                          <Typography fontSize="0.72rem" color="rgba(255,255,255,0.55)">{å.uppföljning}</Typography>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          <Button size="small" onClick={analyze} disabled={loading}
            sx={{ color: 'rgba(255,255,255,0.45)', textTransform: 'none', alignSelf: 'flex-start', fontSize: '0.78rem', '&:hover': { color: '#fff' } }}>
            Kör om analysen
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ── Rubrik-editor ──────────────────────────────────────────────────────────────
function TitleEditor({ session, sessionId }) {
  const [title, setTitle] = useState(session.title || '');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = () => {
    if (!dirty || !title.trim()) return;
    updateDoc(doc(db, 'swot_sessions', sessionId), { title: title.trim() })
      .then(() => { setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2000); });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField fullWidth size="small" value={title}
        onChange={e => { setTitle(e.target.value); setDirty(true); setSaved(false); }}
        onBlur={save}
        onKeyDown={e => e.key === 'Enter' && save()}
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.83rem' } }}
      />
      {saved && <Typography fontSize="0.65rem" color="#4CAF50"
        sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 10 }}>
        Sparat
      </Typography>}
    </Box>
  );
}

// ── Gruppredigerare ────────────────────────────────────────────────────────────
function GroupsEditor({ session, sessionId }) {
  const [groups, setGroups] = useState(session.groups || []);
  const [saving, setSaving] = useState(false);

  // Synka om session.groups uppdateras utifrån (realtid)
  useEffect(() => { setGroups(session.groups || []); }, [session.groups]);

  const persist = async (updated) => {
    setSaving(true);
    await updateDoc(doc(db, 'swot_sessions', sessionId), { groups: updated });
    setSaving(false);
  };

  const addGroup = () => {
    const g = { id: crypto.randomUUID(), name: `Grupp ${groups.length + 1}`, color: GROUP_COLORS[groups.length % GROUP_COLORS.length] };
    const updated = [...groups, g];
    setGroups(updated);
    persist(updated);
  };

  const updateLocal = (id, field, value) =>
    setGroups(gs => gs.map(g => g.id === id ? { ...g, [field]: value } : g));

  const removeGroup = (id) => {
    const updated = groups.filter(g => g.id !== id);
    setGroups(updated);
    persist(updated);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
        <GroupIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', mr: 0.75 }} />
        <Typography fontSize="0.72rem" fontWeight={700} color="rgba(255,255,255,0.45)" letterSpacing={0.8} sx={{ flex: 1 }}>
          GRUPPER
        </Typography>
        {saving && <CircularProgress size={10} sx={{ color: accent, mr: 1 }} />}
        <Button size="small" startIcon={<AddIcon sx={{ fontSize: '0.8rem !important' }} />} onClick={addGroup}
          sx={{ color: accent, textTransform: 'none', fontSize: '0.72rem', p: '2px 6px', minWidth: 0 }}>
          Lägg till
        </Button>
      </Box>

      {groups.length === 0 && (
        <Typography fontSize="0.7rem" color="rgba(255,255,255,0.3)" fontStyle="italic" mb={0.5}>
          Ingen gruppindelning — alla deltar gemensamt
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {groups.map(g => (
          <Box key={g.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <input type="color" value={g.color || accent}
              onChange={e => updateLocal(g.id, 'color', e.target.value)}
              onBlur={() => persist(groups)}
              style={{ width: 28, height: 28, border: 'none', background: 'none', cursor: 'pointer', borderRadius: 4, padding: 0 }}
            />
            <TextField size="small" value={g.name}
              onChange={e => updateLocal(g.id, 'name', e.target.value)}
              onBlur={() => persist(groups)}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { fontSize: '0.8rem' } }}
            />
            <IconButton size="small" onClick={() => removeGroup(g.id)}
              sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ef4444' }, width: 26, height: 26 }}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Inställningspanel ──────────────────────────────────────────────────────────
function SettingsPanel({ session, sessionId, onClose }) {
  const [opacity, setOpacity]   = useState(session.overlayOpacity ?? 60);
  const [blur, setBlur]         = useState(session.panelBlur ?? 12);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [copied, setCopied]     = useState(false);
  const fileRef = useRef(null);

  const saveField = (field, value) => updateDoc(doc(db, 'swot_sessions', sessionId), { [field]: value });

  const handleBgUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const path = `swot_sessions/${sessionId}/bg_${Date.now()}_${file.name}`;
    const sRef = storageRef(storage, path);
    const task = uploadBytesResumable(sRef, file);
    await new Promise((res, rej) => task.on('state_changed',
      s => setUploadPct(Math.round(s.bytesTransferred / s.totalBytes * 100)),
      rej, res,
    ));
    const url = await getDownloadURL(sRef);
    await saveField('backgroundUrl', url);
    setUploading(false);
  };

  const panelLabel = (text) => (
    <Typography fontSize="0.72rem" fontWeight={700} color="rgba(255,255,255,0.45)" letterSpacing={0.8} mb={0.75}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5,
      '& .MuiOutlinedInput-root': {
        color: '#fff',
        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&.Mui-focused fieldset': { borderColor: accent },
      },
      '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography fontWeight={700} color="#fff">Inställningar</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Rubrik */}
      <Box>
        {panelLabel('RUBRIK')}
        <TitleEditor session={session} sessionId={sessionId} />
      </Box>

      {/* Grupper */}
      <GroupsEditor session={session} sessionId={sessionId} />

      {/* Status */}
      <Box>
        {panelLabel('STATUS')}
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {[
            { value: 'collecting', label: 'Insamling', color: '#4CAF50' },
            { value: 'reviewing',  label: 'Granskning', color: '#FF9800' },
            { value: 'done',       label: 'Klar',       color: '#42A5F5' },
          ].map(s => (
            <Button key={s.value} size="small"
              variant={session.status === s.value ? 'contained' : 'outlined'}
              onClick={() => saveField('status', s.value)}
              sx={{
                textTransform: 'none', fontSize: '0.72rem', flex: 1,
                ...(session.status === s.value
                  ? { bgcolor: s.color, '&:hover': { bgcolor: s.color }, border: `1px solid ${s.color}` }
                  : { color: s.color, borderColor: s.color + '88', '&:hover': { borderColor: s.color, bgcolor: s.color + '11' } }
                ),
              }}
            >
              {s.label}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Dela */}
      <Box>
        {panelLabel('DELA BOARD')}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          <Box sx={{
            flex: 1, px: 1.25, py: 0.75, bgcolor: 'rgba(255,255,255,0.06)',
            borderRadius: 1, fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)',
            fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            {window.location.href}
          </Box>
          <Tooltip title={copied ? 'Kopierat!' : 'Kopiera länk'}>
            <IconButton size="small" onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true); setTimeout(() => setCopied(false), 2500);
            }} sx={{ color: copied ? '#4CAF50' : accent }}>
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
        <Typography fontSize="0.68rem" color="rgba(255,255,255,0.3)" mt={0.75} sx={{ lineHeight: 1.4 }}>
          Inloggade konsulter kan öppna boardet via länken.
        </Typography>
      </Box>

      {/* Bakgrundsbild */}
      <Box>
        {panelLabel('BAKGRUNDSBILD')}
        {session.backgroundUrl && (
          <Box sx={{ position: 'relative', mb: 1 }}>
            <Box component="img" src={session.backgroundUrl} alt="Bakgrund"
              sx={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 1.5 }} />
            <Button size="small" onClick={() => saveField('backgroundUrl', '')}
              sx={{ position: 'absolute', top: 4, right: 4, minWidth: 0, p: 0.5, bgcolor: 'rgba(0,0,0,0.6)',
                color: '#fff', '&:hover': { bgcolor: '#ef4444' }, fontSize: '0.65rem', textTransform: 'none' }}>
              Ta bort
            </Button>
          </Box>
        )}
        <input type="file" ref={fileRef} hidden accept="image/*"
          onChange={e => handleBgUpload(e.target.files?.[0])} />
        <Button
          size="small" startIcon={uploading ? <CircularProgress size={12} /> : <WallpaperIcon />}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          variant="outlined"
          sx={{ color: accent, borderColor: accent, textTransform: 'none', fontSize: '0.78rem',
            '&:hover': { bgcolor: accent + '11' } }}
        >
          {uploading ? `${uploadPct}%` : session.backgroundUrl ? 'Byt bild' : 'Välj bild'}
        </Button>
      </Box>

      {/* Overlay-opacitet */}
      <Box>
        <Typography fontSize="0.72rem" fontWeight={700} color="rgba(255,255,255,0.45)" letterSpacing={0.8} mb={0.5}>
          PANEL-TRANSPARENS  <span style={{ color: accent }}>{opacity}%</span>
        </Typography>
        <Slider value={opacity} min={10} max={95}
          onChange={(_, v) => setOpacity(v)}
          onChangeCommitted={(_, v) => saveField('overlayOpacity', v)}
          sx={{ color: accent,
            '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.15)' },
            '& .MuiSlider-thumb': { bgcolor: accent },
          }} />
      </Box>

      {/* Blur */}
      <Box>
        <Typography fontSize="0.72rem" fontWeight={700} color="rgba(255,255,255,0.45)" letterSpacing={0.8} mb={0.5}>
          BLUR  <span style={{ color: accent }}>{blur}px</span>
        </Typography>
        <Slider value={blur} min={0} max={24}
          onChange={(_, v) => setBlur(v)}
          onChangeCommitted={(_, v) => saveField('panelBlur', v)}
          sx={{ color: accent,
            '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.15)' },
            '& .MuiSlider-thumb': { bgcolor: accent },
          }} />
      </Box>

      {/* Frågeställning */}
      <Box>
        {panelLabel('FRÅGESTÄLLNING')}
        <QuestionEditor session={session} sessionId={sessionId} />
      </Box>
    </Box>
  );
}

// ── Frågeställning inline editor ───────────────────────────────────────────────
function QuestionEditor({ session, sessionId }) {
  const [q, setQ]         = useState(session.question || '');
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = () => {
    if (!dirty) return;
    updateDoc(doc(db, 'swot_sessions', sessionId), { question: q })
      .then(() => { setDirty(false); setSaved(true); setTimeout(() => setSaved(false), 2000); });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField multiline minRows={2} maxRows={5} fullWidth size="small"
        value={q}
        onChange={e => { setQ(e.target.value); setDirty(true); setSaved(false); }}
        onBlur={save}
        placeholder="Vad är er strategiska frågeställning?"
        sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.83rem' } }}
      />
      {saved && <Typography fontSize="0.65rem" color="#4CAF50" sx={{ position: 'absolute', bottom: 8, right: 10 }}>Sparat</Typography>}
      {dirty && !saved && <Typography fontSize="0.65rem" color="rgba(255,255,255,0.3)" sx={{ position: 'absolute', bottom: 8, right: 10 }}>Klicka utanför</Typography>}
    </Box>
  );
}

// ── Huvud-komponent ────────────────────────────────────────────────────────────
const TEMPLATES = [
  { id: 'classic',  label: '2×2',      Icon: GridViewIcon },
  { id: 'columns',  label: 'Kolumner', Icon: ViewWeekIcon },
  { id: 'vivid',    label: 'Vivid',    Icon: StyleIcon },
];

export default function SwotNBoard() {
  const { id }         = useParams();
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userProfile } = useAuth();
  const companyLogo = useCompanyLogo();

  const [session,      setSession]      = useState(null);
  const [cards,        setCards]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [presentMode,  setPresentMode]  = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showTimer,    setShowTimer]    = useState(false);
  const [showVoting,   setShowVoting]   = useState(false);
  const [showGuide,    setShowGuide]    = useState(() => !localStorage.getItem('swotn-guide-dismissed'));

  // Timer: { quadrant, totalSeconds } | null
  const [activeTimer, setActiveTimer] = useState(null);

  // Dialog: lägg till / redigera kort
  const [cardDialog, setCardDialog] = useState({ open: false, quadrant: null, card: null });
  const [cardText, setCardText]     = useState('');
  const [savingCard, setSavingCard] = useState(false);

  // Lyssna på session
  useEffect(() => {
    return onSnapshot(doc(db, 'swot_sessions', id), snap => {
      if (!snap.exists()) { navigate('/swotn'); return; }
      setSession({ id: snap.id, ...snap.data() });
      setLoading(false);
    });
  }, [id, navigate]);

  // Lyssna på kort
  useEffect(() => {
    return onSnapshot(collection(db, 'swot_sessions', id, 'cards'), snap => {
      setCards(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
      );
    });
  }, [id]);

  // Om workshopId i URL → importera WS-svar automatiskt (om inga kort ännu)
  useEffect(() => {
    const wsId = searchParams.get('workshopId');
    if (!wsId || cards.length > 0 || loading) return;

    const importFromWorkshop = async () => {
      const wsSnap = await getDocs(collection(db, 'workshops', wsId, 'responses')).catch(() => null);
      if (!wsSnap) return;
      const wsDoc = await import('firebase/firestore').then(({ getDoc, doc: fDoc }) =>
        getDoc(fDoc(db, 'workshops', wsId))
      );
      const wsData = wsDoc.data();
      const questions = wsData?.config?.questions || [];
      const allText = [];
      wsSnap.forEach(r => {
        const answers = r.data()?.answers || [];
        answers.forEach((a, qi) => {
          if (a && typeof a === 'string' && a.trim()) {
            allText.push({ text: a.trim(), question: questions[qi]?.text || `Fråga ${qi + 1}` });
          }
        });
      });
      if (allText.length === 0) return;

      // AI-kategorisering
      const prompt = `Kategorisera dessa anteckningar från en workshop i S/W/O/T. Returnera ENBART JSON (inga kodblock):
{"kategoriserade": [{"text": "...", "kvadrant": "strength|weakness|opportunity|threat"}]}

Anteckningar:\n${allText.map(t => `- ${t.text}`).join('\n')}`;

      try {
        const data = await callClaude({ model: 'claude-sonnet-4-6', max_tokens: 2000, messages: [{ role: 'user', content: prompt }] });
        const parsed = JSON.parse((data.content?.[0]?.text || '').replace(/```json|```/g, '').trim());
        const batch = writeBatch(db);
        (parsed.kategoriserade || []).forEach(item => {
          if (!QUADS.includes(item.kvadrant)) return;
          const cardRef = doc(collection(db, 'swot_sessions', id, 'cards'));
          batch.set(cardRef, {
            quadrant: item.kvadrant, text: item.text,
            author: 'WorkshopN import', source: 'workshopN',
            createdAt: serverTimestamp(),
          });
        });
        await batch.commit();
        await updateDoc(doc(db, 'swot_sessions', id), { workshopId: wsId });
      } catch {}
    };
    importFromWorkshop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, loading]);

  const handleTemplateChange = (templateId) => {
    updateDoc(doc(db, 'swot_sessions', id), { templateId });
  };

  const openAddCard = (quadrant) => {
    setCardDialog({ open: true, quadrant, card: null });
    setCardText('');
  };

  const openEditCard = (card) => {
    setCardDialog({ open: true, quadrant: card.quadrant, card });
    setCardText(card.text);
  };

  const moveCard = async (cardId, newQuadrant) => {
    await updateDoc(doc(db, 'swot_sessions', id, 'cards', cardId), { quadrant: newQuadrant });
  };

  const toggleStar = async (cardId, quadrant) => {
    if (!currentUser?.uid) return;
    const uid = currentUser.uid;
    const cardRef = doc(db, 'swot_sessions', id, 'cards', cardId);
    const cardData = cards.find(c => c.id === cardId);
    const stars = cardData?.stars || [];
    if (stars.includes(uid)) {
      await updateDoc(cardRef, { stars: arrayRemove(uid) });
    } else {
      const quadrantStarCount = cards.filter(c => c.quadrant === quadrant && (c.stars || []).includes(uid)).length;
      if (quadrantStarCount >= 3) return;
      await updateDoc(cardRef, { stars: arrayUnion(uid) });
    }
  };

  const saveCard = async () => {
    if (!cardText.trim()) return;
    setSavingCard(true);
    try {
      if (cardDialog.card) {
        await updateDoc(doc(db, 'swot_sessions', id, 'cards', cardDialog.card.id), { text: cardText.trim() });
      } else {
        await addDoc(collection(db, 'swot_sessions', id, 'cards'), {
          quadrant: cardDialog.quadrant,
          text: cardText.trim(),
          author: userProfile?.displayName || userProfile?.name || currentUser?.displayName || 'Facilitator',
          source: 'live',
          createdAt: serverTimestamp(),
        });
        await updateDoc(doc(db, 'swot_sessions', id), { cardCount: increment(1) });
      }
      setCardDialog({ open: false, quadrant: null, card: null });
      setCardText('');
    } finally {
      setSavingCard(false);
    }
  };

  const deleteCard = async (cardId) => {
    await deleteDoc(doc(db, 'swot_sessions', id, 'cards', cardId));
    await updateDoc(doc(db, 'swot_sessions', id), { cardCount: increment(-1) });
  };

  const cycleRank = async (cardId, currentRank) => {
    const idx = MEDAL_CYCLE.indexOf(currentRank);
    const next = MEDAL_CYCLE[(idx + 1) % MEDAL_CYCLE.length];
    await updateDoc(doc(db, 'swot_sessions', id, 'cards', cardId), { rank: next || null });
  };

  const computeRanksFromVotes = async () => {
    const batch = writeBatch(db);
    QUADS.forEach(q => {
      const qCards = cards.filter(c => c.quadrant === q);
      const sorted = [...qCards].sort((a, b) => (b.stars?.length || 0) - (a.stars?.length || 0));
      sorted.forEach((c, i) => {
        const rank = i === 0 && (c.stars?.length || 0) > 0 ? 'gold'
          : i === 1 && (c.stars?.length || 0) > 0 ? 'silver'
          : i === 2 && (c.stars?.length || 0) > 0 ? 'bronze'
          : null;
        batch.update(doc(db, 'swot_sessions', id, 'cards', c.id), { rank: rank || null });
      });
    });
    await batch.commit();
  };

  const clearAllRanks = async () => {
    const batch = writeBatch(db);
    cards.forEach(c => {
      batch.update(doc(db, 'swot_sessions', id, 'cards', c.id), { rank: null });
    });
    await batch.commit();
  };

  if (loading || !session) return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  const templateProps = {
    cards,
    presentMode,
    currentUserId: currentUser?.uid,
    onToggleStar: toggleStar,
    panelOpacity: session.overlayOpacity ?? 60,
    panelBlur: session.panelBlur ?? 12,
    onAddCard: openAddCard,
    onDeleteCard: deleteCard,
    onEditCard: openEditCard,
    onMoveCard: moveCard,
    onCycleRank: cycleRank,
  };

  const renderTemplate = () => {
    switch (session.templateId) {
      case 'columns': return <ColumnsTemplate {...templateProps} />;
      case 'vivid':   return <VividTemplate   {...templateProps} />;
      default:        return <ClassicTemplate {...templateProps} />;
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: session.backgroundUrl
        ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${session.backgroundUrl}) center/cover no-repeat fixed`
        : '#0a0a0a',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      {!presentMode && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5,
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexWrap: 'wrap',
        }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: 'rgba(255,255,255,0.6)', p: 0.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          {companyLogo && (
            <Box component="img" src={companyLogo} alt="Logotyp"
              sx={{ height: 28, maxWidth: 100, objectFit: 'contain', opacity: 0.85, flexShrink: 0 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography fontWeight={700} fontSize="0.95rem" color="#fff" noWrap>{session.title}</Typography>
              <Chip
                size="small"
                icon={session.mode === 'hybrid'
                  ? <PrintIcon sx={{ fontSize: '0.7rem !important', color: '#42A5F5 !important' }} />
                  : <DevicesIcon sx={{ fontSize: '0.7rem !important', color: 'rgba(255,255,255,0.5) !important' }} />}
                label={session.mode === 'hybrid' ? 'Hybrid' : 'Digitalt'}
                sx={{
                  height: 18, fontSize: '0.6rem', fontWeight: 600, flexShrink: 0,
                  bgcolor: session.mode === 'hybrid' ? '#42A5F522' : 'rgba(255,255,255,0.08)',
                  color: session.mode === 'hybrid' ? '#42A5F5' : 'rgba(255,255,255,0.45)',
                }}
              />
            </Box>
            {session.question && (
              <Typography fontSize="0.72rem" color="rgba(255,255,255,0.4)" noWrap>{session.question}</Typography>
            )}
          </Box>

          {/* Template-väljare */}
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, p: 0.5 }}>
            {TEMPLATES.map(t => {
              const active = (session.templateId || 'classic') === t.id;
              return (
                <Tooltip key={t.id} title={t.label}>
                  <IconButton
                    size="small"
                    onClick={() => handleTemplateChange(t.id)}
                    sx={{
                      color: active ? '#fff' : 'rgba(255,255,255,0.35)',
                      bgcolor: active ? accent : 'transparent',
                      borderRadius: 1.5,
                      p: 0.75,
                      '&:hover': { bgcolor: active ? accent : 'rgba(255,255,255,0.1)' },
                      transition: 'all 0.15s',
                    }}
                  >
                    <t.Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>

          {/* Knappar */}
          <SwotNHelpButton audience="facilitator" />

          <Tooltip title="Skriv ut hybridblad">
            {session.mode === 'hybrid' ? (
              <Button size="small" startIcon={<PrintIcon sx={{ fontSize: '0.85rem !important' }} />}
                onClick={() => navigate(`/swotn/${id}/print`)}
                variant="outlined"
                sx={{ color: '#42A5F5', borderColor: '#42A5F566', textTransform: 'none', fontSize: '0.75rem',
                  fontWeight: 600, borderRadius: 1.5, px: 1.5,
                  '&:hover': { bgcolor: '#42A5F511', borderColor: '#42A5F5' } }}>
                Skriv ut blad
              </Button>
            ) : (
              <IconButton size="small" onClick={() => navigate(`/swotn/${id}/print`)}
                sx={{ color: 'rgba(255,255,255,0.5)', borderRadius: 1.5, '&:hover': { color: '#fff' } }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            )}
          </Tooltip>

          <Tooltip title="Inställningar">
            <IconButton size="small" onClick={() => { setShowSettings(s => !s); setShowAnalysis(false); setShowTimer(false); setShowVoting(false); }}
              sx={{ color: showSettings ? accent : 'rgba(255,255,255,0.5)', bgcolor: showSettings ? accent + '22' : 'transparent', borderRadius: 1.5 }}>
              <TuneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="AI-analys">
            <IconButton size="small" onClick={() => { setShowAnalysis(s => !s); setShowSettings(false); setShowTimer(false); setShowVoting(false); }}
              sx={{ color: showAnalysis ? accent : 'rgba(255,255,255,0.5)', bgcolor: showAnalysis ? accent + '22' : 'transparent', borderRadius: 1.5 }}>
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Timer">
            <IconButton size="small" onClick={() => { setShowTimer(s => !s); setShowSettings(false); setShowAnalysis(false); setShowVoting(false); }}
              sx={{ color: showTimer ? '#4CAF50' : 'rgba(255,255,255,0.5)', bgcolor: showTimer ? '#4CAF5022' : 'transparent', borderRadius: 1.5 }}>
              <TimerIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rankning">
            <IconButton size="small" onClick={() => { setShowVoting(s => !s); setShowSettings(false); setShowAnalysis(false); setShowTimer(false); }}
              sx={{ color: showVoting ? '#FFD700' : 'rgba(255,255,255,0.5)', bgcolor: showVoting ? '#FFD70022' : 'transparent', borderRadius: 1.5 }}>
              <EmojiEventsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Läge-toggle */}
          <Box sx={{ display: 'flex', bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 2, p: 0.4, gap: 0.4 }}>
            {[
              { mode: false, label: 'Arbete', Icon: BuildIcon },
              { mode: true,  label: 'Presentation', Icon: PresentToAllIcon },
            ].map(({ mode, label, Icon }) => (
              <Tooltip key={label} title={label}>
                <IconButton size="small" onClick={() => setPresentMode(mode)}
                  sx={{
                    color: presentMode === mode ? '#fff' : 'rgba(255,255,255,0.35)',
                    bgcolor: presentMode === mode ? (mode ? '#4CAF50' : '#42A5F5') : 'transparent',
                    borderRadius: 1.5, p: 0.75,
                    transition: 'all 0.15s',
                  }}>
                  <Icon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Fasilitatorsguide (kollapsar) ────────────────────────────── */}
      {!presentMode && (
        <Collapse in={showGuide}>
          <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2, pb: 0.5 }}>
              <Typography fontSize="0.7rem" fontWeight={700} color="rgba(255,255,255,0.3)" letterSpacing={1}>
                FASILITATORSGUIDE
              </Typography>
              <Tooltip title="Stäng — öppna igen via ? ikonen i verktygsfältet">
                <IconButton size="small"
                  onClick={() => { setShowGuide(false); localStorage.setItem('swotn-guide-dismissed', '1'); }}
                  sx={{ color: 'rgba(255,255,255,0.3)' }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <SwotNGuide audience="facilitator" compact />
          </Box>
        </Collapse>
      )}

      {/* ── Present mode: logobanner ─────────────────────────────────── */}
      {presentMode && companyLogo && (
        <Box sx={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          px: 3, py: 1,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}>
          <Box component="img" src={companyLogo} alt="Logotyp"
            sx={{ height: 32, maxWidth: 120, objectFit: 'contain', opacity: 0.75 }} />
        </Box>
      )}

      {/* ── Present mode: tryck ESC att gå ur ─────────────────────────── */}
      {presentMode && (
        <Box sx={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          display: 'flex', gap: 1, zIndex: 100,
        }}>
          <Button
            onClick={() => setPresentMode(false)}
            size="small"
            startIcon={<BuildIcon />}
            sx={{
              bgcolor: 'rgba(0,0,0,0.75)', color: '#fff', textTransform: 'none',
              backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 2, fontSize: '0.78rem', fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
            }}
          >
            Avsluta presentation
          </Button>
        </Box>
      )}

      {/* ── Hybrid-guide (kollapsar om man stänger) ──────────────────────── */}
      {!presentMode && session.mode === 'hybrid' && cards.length === 0 && (
        <Box sx={{
          mx: 2, mt: 1.5,
          p: '10px 16px',
          borderRadius: '10px',
          background: 'rgba(66,165,245,0.1)',
          border: '1px solid rgba(66,165,245,0.25)',
          backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <PrintIcon sx={{ color: '#42A5F5', fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography fontSize="0.8rem" fontWeight={700} color="#42A5F5" mb={0.25}>
              Hybridläge — tre steg
            </Typography>
            <Typography fontSize="0.72rem" color="rgba(255,255,255,0.65)" sx={{ lineHeight: 1.5 }}>
              ① Skriv ut hybridblad — ett per SWOT-kvadrant (× grupp om grupper finns)&nbsp;&nbsp;
              ② Lägg ut bladen, deltagarna skriver för hand&nbsp;&nbsp;
              ③ Skanna varje blad med kameran — AI extraherar bidragen direkt hit
            </Typography>
          </Box>
          <Button size="small" startIcon={<PrintIcon />}
            onClick={() => navigate(`/swotn/${id}/print`)}
            variant="contained"
            sx={{ bgcolor: '#42A5F5', '&:hover': { bgcolor: '#1e88e5' }, textTransform: 'none',
              fontWeight: 600, fontSize: '0.78rem', flexShrink: 0 }}>
            Skriv ut blad nu
          </Button>
        </Box>
      )}

      {/* ── Huvud-layout ─────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

        {/* Board */}
        <Box sx={{ flex: 1, p: 2, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {renderTemplate()}
          </Box>

          {/* ── In-board timer circle ────────────────────────────────────── */}
          {activeTimer && (
            <BoardTimerCircle
              quadrant={activeTimer.quadrant}
              totalSeconds={activeTimer.totalSeconds}
              onClose={() => setActiveTimer(null)}
            />
          )}
        </Box>

        {/* Sidopanel: Inställningar */}
        {!presentMode && showSettings && (
          <Box sx={{
            width: 300, flexShrink: 0,
            background: 'rgba(10,10,10,0.92)',
            backdropFilter: 'blur(16px)',
            borderLeft: '1px solid rgba(255,255,255,0.06)',
            overflowY: 'auto',
          }}>
            <SettingsPanel session={session} sessionId={id} onClose={() => setShowSettings(false)} />
          </Box>
        )}

        {/* Dialog: AI-analys */}
        <Dialog open={!presentMode && showAnalysis} onClose={() => setShowAnalysis(false)} maxWidth="md" fullWidth
          PaperProps={{ sx: {
            bgcolor: '#0e0e0e', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '14px', maxHeight: '90vh',
          }}}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1,
            borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AutoAwesomeIcon sx={{ color: accent, fontSize: 20 }} />
              <Typography fontWeight={700} fontSize="1rem" color="#fff">AI-analys</Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowAnalysis(false)} sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <SwotAIAnalysis session={session} cards={cards} />
          </DialogContent>
        </Dialog>

        {/* Sidopanel: Timer */}
        {!presentMode && showTimer && (
          <Box sx={{
            width: 270, flexShrink: 0,
            background: 'rgba(15,15,15,0.55)',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            overflowY: 'auto',
            p: 2.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
                <Typography fontWeight={700} color="#fff">Timer</Typography>
              </Box>
              <IconButton size="small" onClick={() => setShowTimer(false)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography fontSize="0.68rem" color="rgba(255,255,255,0.4)" mb={1.5}>
              Ange tid per kvadrant och starta timern. Tid sparas automatiskt.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {QUADS.map(q => {
                const info = QUAD[q];
                const defaultSecs = session.timerDurations?.[q] ?? 480;
                const mins = Math.round(defaultSecs / 60);
                return (
                  <Box key={q} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: '9px', flexShrink: 0,
                      bgcolor: hexToRgba(info.color, 0.18),
                      border: `1.5px solid ${hexToRgba(info.color, 0.4)}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <info.Icon sx={{ fontSize: 18, color: info.color }} />
                    </Box>
                    <Typography fontSize="0.78rem" color="#fff" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {info.label}
                    </Typography>
                    <Box
                      component="input"
                      type="number" min={1} max={60}
                      defaultValue={mins}
                      key={`${q}-${mins}`}
                      onBlur={e => {
                        const v = Math.max(1, Math.min(60, parseInt(e.target.value) || mins));
                        updateDoc(doc(db, 'swot_sessions', id), {
                          [`timerDurations.${q}`]: v * 60,
                        });
                      }}
                      sx={{
                        width: 46, textAlign: 'center', fontSize: '0.82rem',
                        bg: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '6px', color: '#fff', p: '4px 0',
                        '&:focus': { outline: `1px solid ${info.color}` },
                      }}
                      style={{
                        width: 46, textAlign: 'center', fontSize: '0.82rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 6, color: '#fff', padding: '4px 0',
                        outline: 'none',
                      }}
                    />
                    <Typography fontSize="0.68rem" color="rgba(255,255,255,0.35)" sx={{ flexShrink: 0 }}>min</Typography>
                    <IconButton size="small"
                      onClick={() => setActiveTimer({ quadrant: q, totalSeconds: defaultSecs })}
                      sx={{
                        width: 30, height: 30, bgcolor: '#4CAF5022',
                        border: '1px solid #4CAF5044',
                        color: '#4CAF50', borderRadius: '8px',
                        '&:hover': { bgcolor: '#4CAF5033', borderColor: '#4CAF50' },
                        flexShrink: 0,
                      }}>
                      <PlayArrowIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Sidopanel: Rankning */}
        {!presentMode && showVoting && (
          <Box sx={{
            width: 270, flexShrink: 0,
            background: 'rgba(15,15,15,0.55)',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
            borderLeft: '1px solid rgba(255,255,255,0.08)',
            overflowY: 'auto',
            p: 2.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 18 }} />
                <Typography fontWeight={700} color="#fff">Rankning</Typography>
              </Box>
              <IconButton size="small" onClick={() => setShowVoting(false)} sx={{ color: 'rgba(255,255,255,0.4)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Typography fontSize="0.68rem" color="rgba(255,255,255,0.4)" mb={2} sx={{ lineHeight: 1.55 }}>
              Klicka på trofé-ikonen på ett kort för att sätta guld/silver/brons manuellt — eller beräkna automatiskt från röster (stjärnor).
            </Typography>

            {/* Per-kvadrant sammanfattning */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {QUADS.map(q => {
                const info = QUAD[q];
                const qCards = cards.filter(c => c.quadrant === q);
                const ranked = qCards.filter(c => c.rank);
                const totalVotes = qCards.reduce((sum, c) => sum + (c.stars?.length || 0), 0);
                return (
                  <Box key={q} sx={{ p: 1.25, borderRadius: 1.5, border: `1px solid ${info.color}22`, bgcolor: info.color + '08' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <info.Icon sx={{ fontSize: 14, color: info.color }} />
                      <Typography fontSize="0.72rem" fontWeight={700} color={info.color} sx={{ flex: 1 }}>{info.label}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {['gold', 'silver', 'bronze'].map(r => {
                          const m = MEDAL[r];
                          return ranked.find(c => c.rank === r)
                            ? <EmojiEventsIcon key={r} sx={{ fontSize: 12, color: m.color }} />
                            : null;
                        })}
                      </Box>
                    </Box>
                    <Typography fontSize="0.65rem" color="rgba(255,255,255,0.3)" mt={0.25}>
                      {qCards.length} kort · {ranked.length} rankade{totalVotes > 0 ? ` · ${totalVotes} röster` : ''}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: '0.95rem !important' }} />}
              onClick={computeRanksFromVotes}
              disabled={!cards.some(c => (c.stars?.length || 0) > 0)}
              sx={{
                textTransform: 'none', borderColor: '#FFD70066', color: '#FFD700', mb: 1, fontSize: '0.8rem',
                '&:hover': { borderColor: '#FFD700', bgcolor: '#FFD70011' },
                '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.2)' },
              }}
            >
              Beräkna från röster
            </Button>
            <Button
              fullWidth size="small"
              onClick={clearAllRanks}
              sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem',
                '&:hover': { color: '#ef4444', bgcolor: '#ef444411' } }}
            >
              Rensa alla rankningar
            </Button>
          </Box>
        )}
      </Box>


      {/* ── Dialog: Lägg till / redigera kort ──────────────────────────── */}
      <Dialog open={cardDialog.open} onClose={() => { if (!savingCard) setCardDialog({ open: false, quadrant: null, card: null }); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          {cardDialog.card ? 'Redigera kort' : `Lägg till — ${cardDialog.quadrant ? QUAD[cardDialog.quadrant]?.label : ''}`}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth multiline rows={3}
            value={cardText} onChange={e => setCardText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveCard(); }}
            placeholder="Skriv ditt bidrag…"
            size="small" sx={{ mt: 1.5 }}
          />
          <Typography fontSize="0.68rem" color="text.disabled" mt={0.5}>Ctrl+Enter för att spara</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCardDialog({ open: false, quadrant: null, card: null })} disabled={savingCard}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={saveCard}
            disabled={!cardText.trim() || savingCard}
            startIcon={savingCard ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <CheckIcon />}
            sx={{ bgcolor: accent, '&:hover': { bgcolor: '#C94D00' }, textTransform: 'none', fontWeight: 600 }}
          >
            {savingCard ? 'Sparar…' : cardDialog.card ? 'Uppdatera' : 'Lägg till'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
